import {useNaviScratchBlockDispatch} from "../../../navi/Navi.tsx";
import {useBundleById, useSurchargesForSubject, useWastesForBundle} from "../../../rpc/webrpc-client.gen.ts";
import {useCallback, useEffect, useMemo, useState} from "react";
import {facetIcons} from "../../../components/Facet.tsx";
import {createFileRoute, Navigate, useNavigate} from "@tanstack/react-router";
import {useBomb} from "../../../bomb/bomb.tsx";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {Box, Button, Stack, ToggleButtonGroup} from "@mui/joy";
import {
    RingedToggleButton,
    RingedToggleButtonProps,
    ToggleBar,
    ToggleBarIcon,
    toggleSwitchButtonSx,
    toggleSwitchSx
} from "../../../components/ToggleBar.tsx";
import {useSelf} from "../../../agent/useSelf.ts";
import {BombTile, TileSpread} from "../../../components/Tile.tsx";
import {usePreferences} from "../../../agent/usePreferences.ts";
import {useQueries, useQueryClient} from "@tanstack/react-query";
import {wasteStatusTqo} from "../../../waste/waste.tsx";
import {mergeUseQueriesWithInfill} from "../../../tq/mergeUseQueriesWithInfill.tsx";
import {indexesOf, NADA} from "../../../util/arutil.tsx";
import {InspectorWasteTile} from "../../../inspector/InspectorWasteTile.tsx";
import {SurchargeTile} from "../../../surcharge/tile.tsx";
import {useTranslation} from "react-i18next";
import {InspectionStageOrSurcharge} from "../../../inspect/stages.tsx";
import {LiveTieredColor} from "../../../util/defaultPrefs.tsx";
import {useMothership} from "../../../tq/tqx.tsx";
import {SpawnTileFood} from "../../../bundle/cargo.tsx";
import {SpawnWasteTile} from "../../../waste/SpawnWasteTile.tsx";

function BundleInspectPage() {
    const {id: bundleId} = Route.useParams();
    const {t} = useTranslation();
    const tqc = useQueryClient();
    const mama = useMothership();
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const dispatchScratchNaviItem = useNaviScratchBlockDispatch();
    const bundleTqrv = useBundleById({bundleId});
    const bundle = bundleTqrv.data;
    useEffect(() => {
        if (bundle) {
            dispatchScratchNaviItem({
                to: Route.to,
                params: {id: bundleId},
                icon: {icon: facetIcons.inspect},
                children: bundle.displayName,
            });
        }
    }, [dispatchScratchNaviItem, bundle, bundleId]);

    const prefsTqrv = usePreferences();
    const [activeTierColors, setActiveTierColors] = useState<Map<InspectionStageOrSurcharge, LiveTieredColor<InspectionStageOrSurcharge>>>(new Map<InspectionStageOrSurcharge, LiveTieredColor<InspectionStageOrSurcharge>>());
    const resetTierColors = useCallback(() => {
        if (prefsTqrv.data) {
            const iscEntries = prefsTqrv.data.inspectionTierColors ?? NADA;
            const ixen = indexesOf(iscEntries);
            const map = new Map<InspectionStageOrSurcharge, LiveTieredColor<InspectionStageOrSurcharge>>();
            for (const ix of ixen) {
                const frozenEntry = iscEntries[ix];
                if (frozenEntry) {
                    const liveEntry: LiveTieredColor<InspectionStageOrSurcharge>
                        = {...frozenEntry, sortIndex: ix + 1};
                    map.set(liveEntry.tier, liveEntry);
                }
            }

            setActiveTierColors(map);
        }
    }, [prefsTqrv.data]);

    useEffect(() => {
        if (activeTierColors.size === 0 && prefsTqrv.data) {
            resetTierColors();
        }
    }, [activeTierColors.size, prefsTqrv.data, resetTierColors]);

    const toggleTierWatchers = useMemo(() => {
        const tiers = Array.from(activeTierColors.keys());
        return new Map(
            tiers.map(tier => {
                const rv: RingedToggleButtonProps["onChange"] = (_, value) => {
                    const nextMap = new Map<InspectionStageOrSurcharge, LiveTieredColor<InspectionStageOrSurcharge>>(activeTierColors);
                    const prev = nextMap.get(tier);
                    if (prev) {
                        const next = {...prev, watched: value} as const;
                        nextMap.set(tier, next);
                        setActiveTierColors(nextMap);
                    }
                };
                return [tier, rv];
            }));
    }, [activeTierColors]);

    const surchargeTierColor = activeTierColors.get("SURCHARGE");

    const wastesTqrv = useWastesForBundle({bundleId});
    const wastes = wastesTqrv.data;
    const wasteStatusenTqrv = useQueries(wastesTqrv.data
        ? {
            queries: wastesTqrv.data.map(waste => wasteStatusTqo({tqc, mama, args: {wasteId: waste.id}})),
            combine: mergeUseQueriesWithInfill,
        }
        : {queries: NADA});
    const wasteStatuses = wasteStatusenTqrv.data;
    const wasteTileFoods = wastes
        ? indexesOf(wastes).map(ix => ({
            flavor: "waste", waste: wastes[ix], status: wasteStatuses[ix]
        } as const))
        : NADA;

    const surchargesTqrv
        = useSurchargesForSubject(activeTierColors.get("SURCHARGE")?.watched
        ? {subjectClass: "bundle", subjectId: bundleId}
        : undefined);
    const surcharges = surchargesTqrv.data;
    const surchargeTileFoods = surcharges
        ? surcharges.map(surcharge => ({flavor: "surcharge", surcharge} as const))
        : NADA;

    const tileFoods = useMemo(() => {
        const dirtyRaw = [...wasteTileFoods, ...surchargeTileFoods] as const;
        const cleanUnsorted = dirtyRaw.filter(maybe => {
            if (maybe.flavor === "surcharge") {
                // it wouldn't be here if we weren't watching surcharges
                return true;
            }
            if (maybe.status?.inspectionStage && activeTierColors.get(maybe.status.inspectionStage)?.watched) {
                return true;
            }
            return false;
        });
        const sorted = cleanUnsorted.toSorted((a, b) => {
            let aTier = -1;
            if (a.flavor === "surcharge") {
                if (surchargeTierColor?.sortIndex) {
                    aTier = surchargeTierColor.sortIndex;
                }
            } else if (a.status?.inspectionStage) {
                aTier = activeTierColors.get(a.status.inspectionStage)?.sortIndex ?? -1;
            }

            let bTier = -1;
            if (b.flavor === "surcharge") {
                if (surchargeTierColor?.sortIndex) {
                    bTier = surchargeTierColor.sortIndex;
                }
            } else if (b.status?.inspectionStage) {
                bTier = activeTierColors.get(b.status.inspectionStage)?.sortIndex ?? -1;
            }

            return aTier - bTier;
        });

        const spawnTileFood: SpawnTileFood = {flavor: "spawn", bundleId};
        const rv = [...sorted, spawnTileFood];

        return rv;
    }, [activeTierColors, bundleId, surchargeTierColor?.sortIndex, surchargeTileFoods, wasteTileFoods]);

    const navigate = useNavigate();
    const bomb = useBomb();

    const tierToggleBlock = useMemo(() => {
        if (activeTierColors.size > 1) {
            const tiers = Array.from(activeTierColors.keys());
            return <Stack direction="row">
                {tiers.map(tier => {
                    const tc = activeTierColors.get(tier);
                    return <RingedToggleButton key={tier} value={tier}
                                               selected={tc?.watched}
                                               onChange={toggleTierWatchers.get(tier)}
                                               size="lg" color={tc?.color}>
                        {<Stack direction="column" sx={{display: "flex", height: "100%"}}>
                            <Box sx={{flex: 1}}>{t(`inspection.statusLabel.${tier}`)}</Box>
                        </Stack>}
                    </RingedToggleButton>
                })}</Stack>
        } else {
            return null;
        }
    }, [activeTierColors, t, toggleTierWatchers]);

    if (selfTqrv.isPending) {
        return <SuspenseDialog message="who am i?"/>
    }

    if (!self?.inspectorFor && !self?.supervisorFor) {
        return <Navigate to="/"/>
    }

    if (bundleTqrv.isPending) {
        return <SuspenseDialog message="fetching bundle..."/>
    }

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>
                <ToggleBar>
                    <ToggleButtonGroup color="primary" variant="soft"
                                       value="inspect" onChange={(_, next) => {
                        if (next) {
                            navigate({to: `/bundle/$id/${next}`, params: {id: bundleId}})
                                .catch(bomb);
                        }
                    }} sx={toggleSwitchSx}>
                        <Button sx={toggleSwitchButtonSx} value="inspect">
                            <ToggleBarIcon icon={facetIcons.inspect}/>
                        </Button>
                        <Button sx={toggleSwitchButtonSx} value="timeline">
                            <ToggleBarIcon icon={facetIcons.timeline}/>
                        </Button>
                    </ToggleButtonGroup>

                    {tierToggleBlock}
                </ToggleBar>
            </Stack>
        </PageHeader>
        <PageBody>
            <Stack direction="column" spacing={1.2}>
                <TileSpread foods={tileFoods} Component={({food}) => {
                    if (food.flavor === "waste" && food.waste?.id) {
                        const color = food.status?.inspectionStage
                            && activeTierColors.get(food.status.inspectionStage)?.color;
                        return <InspectorWasteTile wasteId={food.waste.id} color={color}/>;
                    }
                    if (food.flavor === "surcharge" && surchargeTierColor) {
                        return <SurchargeTile surcharge={food.surcharge} color={surchargeTierColor.color}/>;
                    }
                    if (food.flavor === "spawn") {
                        return <SpawnWasteTile bundleId={food.bundleId}/>
                    }
                    return <BombTile/>
                }} estimatedHeight={180}/>
            </Stack>
        </PageBody>
    </PageShell>
        ;
}

export const Route = createFileRoute("/_auth/bundle/$id/inspect")({
    component: BundleInspectPage,
});
